/**
 * CreodeAjaxAPI - A utility class for making AJAX requests to Shopify sections
 * 
 * This class provides methods to fetch data from Shopify sections using AJAX,
 * clean the response data, and return parsed JSON objects.
 * 
 * @class CreodeAjaxAPI
 * @version 1.0.0
 * @author Creode
 */
class CreodeAjaxAPI {

  /**
   * The prefix used for section identifiers
   * @type {string}
   * @readonly
   */
  prefix = 'creode-ajax-api--';

  /**
   * Fetches data from a specified endpoint and returns a clean JSON object
   * 
   * This is the main method that orchestrates the data fetching process.
   * It first gets the raw response string, then cleans and parses it.
   * 
   * @async
   * @param {string} endpoint - The endpoint identifier (without prefix)
   * @param {boolean} cache - Whether to load the data from cache if available and save the data to cache (default: false)
   * @returns {Promise<Object>} A promise that resolves to the parsed JSON object
   * @throws {Error} Throws an error if the fetch request fails or JSON parsing fails
   */
  async getData( endpoint, cache = false ) {
    // Load data from storage
    if (cache) {
      let storedData = this.loadFromStorage( endpoint );
      if (storedData) {
        return storedData;
      }
    }

    // Get the raw HTML response string from the Shopify section
    let responseString = await this.getResponseString( endpoint );
    
    // Clean and parse the response string into a JSON object
    let responseObject = this.getResponseObject( responseString );

    if (Array.isArray(responseObject) && 0 < responseObject.length) {
      let page = 2;
      let paginatedResponseArray = responseObject;

      while (0 < paginatedResponseArray.length) {
        responseString = await this.getResponseString( endpoint, page );
        paginatedResponseArray = this.getResponseObject( responseString );

        responseObject = responseObject.concat(paginatedResponseArray);
        page++;
      }
    }

    // Save data to storage
    if (cache) {
      this.saveToStorage( endpoint, responseObject );
    }

    return responseObject;
  }

  /**
   * Loads data from storage
   * 
   * @param {string} endpoint - The endpoint identifier (without prefix)
   * @returns {Object} The data from storage
   */
  loadFromStorage( endpoint ) {
    let item = localStorage.getItem('creode_ajax_api_data__' + endpoint);

    if (!item) {
      return null;
    }

    let storedData = null;

    try {
      storedData = JSON.parse(item);
    } catch(e) {
      return null;
    }

    const now = new Date();
    if(storedData.expiry < now.getTime()) {
      return null;
    }

    return storedData.data;
  }

  /**
   * Saves data to storage
   * 
   * @param {string} endpoint - The endpoint identifier (without prefix)
   * @param {Object} data - The data to save
   */
  saveToStorage( endpoint, data ) {
    const now = new Date();
    const expiry = now.getTime() + 600000; // 10 minutes
    const storedData = { data: data, expiry: expiry };

    localStorage.setItem('creode_ajax_api_data__' + endpoint, JSON.stringify(storedData));
  }

  /**
   * Fetches the raw response string from a Shopify section
   * 
   * Makes an AJAX request to Shopify's sections API to get the HTML content
   * of a specific section identified by the prefixed endpoint.
   * 
   * @async
   * @param {string} endpoint - The endpoint identifier (without prefix)
   * @param {number} page - The page number to fetch
   * @returns {Promise<string>} A promise that resolves to the raw HTML string
   * @throws {Error} Throws an error if the fetch request fails
   * 
   * @example
   * const responseString = await api.getResponseString('customer');
   */
  async getResponseString( endpoint, page = 1 ) {
    // Construct the Shopify sections API URL with the prefixed endpoint
    const response = await fetch( '/?sections=' + this.prefix + endpoint + '&page=' + page );
    
    // Parse the JSON response from Shopify
    const object = await response.json();

    // Return the specific section content using the prefixed key
    return object[this.prefix + endpoint];
  }

  /**
   * Cleans HTML tags from response string and parses it as JSON
   * 
   * This method removes all HTML tags and newlines from the response string,
   * then attempts to parse the cleaned string as JSON.
   * 
   * @param {string} responseString - The raw HTML response string to clean
   * @returns {Object} The parsed JSON object
   * @throws {SyntaxError} Throws a SyntaxError if the cleaned string is not valid JSON
   * 
   * @example
   * const htmlString = '<div>{"key": "value"}</div>';
   * const jsonObject = api.getResponseObject(htmlString);
   * // Returns: {key: "value"}
   */
  getResponseObject( responseString ) {
    // Remove all HTML tags using regex pattern
    // Remove newline characters for cleaner JSON parsing
    const cleanString = responseString
      .replace(/<[^>]*>?/gm, '') // Remove HTML tags
      .replace('\n', '');        // Remove newlines

    // Parse the cleaned string as JSON and return the object
    return JSON.parse( cleanString );
  }
}

/**
 * Immediately Invoked Function Expression (IIFE) for global initialization
 * 
 * This anonymous function executes immediately when the script loads and
 * creates a global instance of CreodeAjaxAPI available on the window object.
 * This allows other scripts to access the API without needing to instantiate it.
 * 
 * @function
 * @async
 * @global
 * 
 * @example
 * // Usage from other scripts:
 * const data = await window.creodeAjaxAPI.getData('customer');
 */
(async () => {
  // Create a global instance of CreodeAjaxAPI for use across the application
  window.creodeAjaxAPI = new CreodeAjaxAPI();
})();
